import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { UserRoleEnum } from '../../auth/enums/user-role.enum';
import { JwtDecoded } from '../../auth/models/jwt-decoded.model';
import { Nationalities } from '../../shared/models/nationalities/nationalities.model';
import { PageModel } from '../../shared/models/page.model';
import { CreateUser } from '../models/create-user.model';
import { DocumentInfo } from '../models/document-verification.model';
import { ErrorResponse } from '../models/error-response.model';
import { UnblockUser } from '../models/unblock-user.model';
import { UpdateUser } from '../models/update-user.model';
import { UserFilterParams } from '../models/user-filter-params.model';
import { User } from '../models/user.model';
import { AppState } from './app.state';
import { AuthService } from './auth.service';
import { CustomerDocumentsApiService } from './customer-documents-api.service';
import { UsersApiService } from './users-api.service';

@Injectable({
	providedIn: 'root',
})
export class UsersFacade {
	private loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private customerDocumentsApiService: CustomerDocumentsApiService,
		private usersApiService: UsersApiService,
		private authService: AuthService,
		private appState: AppState
	) {}

	isLoading$(): Observable<boolean> {
		return this.loading$.asObservable();
	}

	getNationalities$(): Observable<Nationalities[]> {
		return this.usersApiService.geNationalitiesList$();
	}

	getUsersPage$(pageSize: number = 25, currentPage: number = 1, filterParams?: UserFilterParams): Observable<PageModel<User>> {
		return this.usersApiService.getUsersPage$(pageSize, currentPage, filterParams);
	}

	getById$(id: number): Observable<User> {
		return this.usersApiService.getById$(id);
	}

	createUser$(user: CreateUser): Observable<{ userId: number } | ErrorResponse> {
		this.loading$.next(true);

		return this.usersApiService.createUser$(user).pipe(
			finalize(() => {
				this.loading$.next(false);
			})
		);
	}

	updateUser$(userId: number, user: UpdateUser): Observable<null | ErrorResponse> {
		this.loading$.next(true);

		return this.usersApiService.updateUser$(userId, user).pipe(finalize(() => this.loading$.next(false)));
	}

	deleteUser$(id: number): Observable<null | ErrorResponse> {
		this.loading$.next(true);
		return this.usersApiService.deleteUser$(id).pipe(finalize(() => this.loading$.next(false)));
	}

	getLoggedUser$(): Observable<null | JwtDecoded> {
		const token = this.authService.decodeAccessToken();

		if (!token) {
			return throwError(null);
		}

		this.appState.setLoggedUser(token);

		return of(token);
	}

	setLoggedUser(user: JwtDecoded): void {
		this.appState.setLoggedUser(user);
	}

	userExist$(email: string): Observable<ValidationErrors | null> {
		return this.usersApiService
			.userExists$(email)
			.pipe(switchMap((res: boolean) => (res ? of({ duplicated: true }) : of(null))));
	}

	documentNumberExists$(documentNumber: string): Observable<ValidationErrors | null> {
		return this.customerDocumentsApiService
			.documentNumberExists$(documentNumber)
			.pipe(switchMap((res: boolean) => (res ? of({ documentNumberTaken: true }) : of(null))));
	}

	loggedUser(): JwtDecoded {
		return this.appState.loggedUser();
	}

	toggleLockedUser(userId: number, isLockUserMode: boolean): Observable<null | ErrorResponse> {
		if (isLockUserMode) {
			return this.usersApiService.lockUser$(userId);
		}

		return this.usersApiService.unlockUser$(userId);
	}

	hasAnyOfRequiredRole(requiredRoles: UserRoleEnum[]): boolean {
		if (!requiredRoles.length) {
			return false;
		}

		const token = this.authService.decodeAccessToken();

		for (const role of requiredRoles) {
			if (role === token?.roleId) {
				return true;
			}
		}

		return false;
	}

	unblockUser$(unblockuserData: UnblockUser): Observable<null | ErrorResponse> {
		this.loading$.next(true);

		return this.usersApiService.unblockUser$(unblockuserData).pipe(finalize(() => this.loading$.next(false)));
	}

	getCustomerDocumentInformation$(documentId: string): Observable<DocumentInfo> {
		this.loading$.next(true);

		return this.customerDocumentsApiService.getDocumentInformation$(documentId).pipe(
			finalize(() => {
				this.loading$.next(false);
			})
		);
	}

	createCustomerDocumentInformation$(documentId: string, data: DocumentInfo): Observable<void | ErrorResponse> {
		this.loading$.next(true);

		return this.customerDocumentsApiService.createDocumentInformation$(documentId, data).pipe(
			finalize(() => {
				this.loading$.next(false);
			})
		);
	}

	updateCustomerDocumentInformation$(documentId: string, data: DocumentInfo): Observable<void | ErrorResponse> {
		this.loading$.next(true);

		return this.customerDocumentsApiService.updateDocumentInformation$(documentId, data).pipe(
			finalize(() => {
				this.loading$.next(false);
			})
		);
	}
}
